/* eslint-disable react-refresh/only-export-components */

import { Typography, Box, Stack, Tooltip } from "@mui/material";

export const COMMODITY_META = {
  gold: { label: "Gold", symbol: "Au", color: "#d4af37" },
  silver: { label: "Silver", symbol: "Ag", color: "#c0c0c0" },
  copper: { label: "Copper", symbol: "Cu", color: "#b87333" },
  molybdenum: { label: "Molybdenum", symbol: "Mo", color: "#8b8f99" },
  nickel: { label: "Nickel", symbol: "Ni", color: "#8ac0c8" },
  zinc: { label: "Zinc", symbol: "Zn", color: "#c2d6d6" },
  lead: { label: "Lead", symbol: "Pb", color: "#6b7280" },
  lithium: { label: "Lithium", symbol: "Li", color: "#a3d9ff" },
  sulphur: { label: "Sulphur", symbol: "S", color: "#f0c75e" },
  uranium: { label: "Uranium", symbol: "U", color: "#b2d234" },
};

export const getCommodityMeta = (value) => {
  if (!value) return null;
  const key = String(value).trim().toLowerCase();
  if (!key) return null;
  if (COMMODITY_META[key]) return COMMODITY_META[key];
  if (key.includes("rare")) {
    return { label: "Rare Earths", symbol: "RE", color: "#8d6e63" };
  }
  return {
    label: key.replace(/\b\w/g, (c) => c.toUpperCase()),
    symbol: key.slice(0, 2).toUpperCase(),
    color: "#94a3b8",
  };
};

export const formatTickers = (tickers) => {
  if (!tickers) return "—";
  let list = tickers;
  if (!Array.isArray(list)) {
    const raw = String(list).trim();
    // Try to coerce a Python/JSON-ish string into an array of objects
    if (raw.startsWith("[")) {
      try {
        const coerced = JSON.parse(raw.replace(/'/g, '"'));
        if (Array.isArray(coerced)) {
          list = coerced;
        } else {
          list = raw.split(",");
        }
      } catch {
        list = raw.split(",");
      }
    } else {
      list = raw.split(",");
    }
  }
  const formatted = list
    .map((t) => {
      if (typeof t === "string") {
        const trimmed = t.trim();
        if (!trimmed) return "";
        const parts = trimmed.split(":");
        if (parts.length === 2) return `${parts[0].trim()}:${parts[1].trim()}`;
        return trimmed;
      }
      const ticker = t?.ticker || t?.symbol || "";
      const exch = t?.exchange || t?.exch || "";
      if (!ticker && !exch) return "";
      if (exch && ticker) return `${exch}:${ticker}`.trim();
      return ticker || exch;
    })
    .filter(Boolean);
  return formatted.length ? formatted.join(", ") : "—";
};

export const CommodityDots = ({ commodities }) => {
  if (!commodities || commodities.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.8} flexWrap="wrap" rowGap={0.8} mt={1}>
      {commodities.map((item, idx) => {
        const meta = getCommodityMeta(item);
        if (!meta) return null;
        return (
          <Tooltip key={`${meta.label}-${idx}`} title={meta.label}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: meta.color,
                color: "#0f172a",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.4,
                boxShadow: "0 2px 6px rgba(15, 23, 42, 0.18)",
              }}
            >
              {meta.symbol}
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export const InfoRow = ({ label, value, href }) => {
  const display = value || "—";
  if (href && value) {
    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography
          variant="body2"
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "primary.main", textDecoration: "none", wordBreak: "break-all" }}
        >
          {display}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: "break-word", textAlign: "right" }}>
        {display}
      </Typography>
    </Box>
  );
};

export default {
  COMMODITY_META,
  getCommodityMeta,
  formatTickers,
  CommodityDots,
  InfoRow,
};
